import type { PacketHeader } from '@ipfabric/utils';

const pathLookupHeaderType: { [Key in PacketHeader['type']]: string } = {
  aws: 'AWS',
  azure: 'Azure',
  capwap: 'CAPWAP',
  ethernet: 'Ethernet',
  esp: 'ESP',
  fp: 'FabricPath',
  gre: 'GRE',
  'meraki-vpn': 'Meraki VPN',
  mpls: 'MPLS',
  ip: 'IP',
  ipv6: 'IPv6',
  tcp: 'TCP',
  udp: 'UDP',
  vcmp: 'VCMP',
  vxlan: 'VXLAN',
  icmp: 'ICMP',
  icmpv6: 'ICMPv6',
};

export const deviceViewMessages = {
  en: {
    hostnameTab: 'Detail',
    deviceAttributesTab: 'Attributes',
    resourceHierarchyTab: 'Resource hierarchy',
    resourceHierarchyEmpty: 'No resource hierarchy available',
    devicePathLookupTab: 'Path Inspector',
    setupTablesTooltip: 'Set up tables',
    stpDetailTab: 'STP Instances',
    intentCheckDetailTab: 'Intent Check Detail',
    showInWindow: 'Show in window',
    btnShowLog: 'Log file',
    btnDownloadJson: 'JSON file',
    btnConnect: 'Connect',
    btnConfigFile: 'Config file',
    closeTab: 'Close tab',
    noDataAvailable: 'No data available',
    deviceDoesntExist: "Device doesn't exist in the snapshot",
    unableToLoadData: 'Unable to load data, click for the refresh',
    cantGetConfigFile: "Can't get config file for {hostname}",
    noTablesToShow: 'No tables to show. Please configure table settings.',
    deviceNotInSnapshot: 'This device is not present in current snapshot.',
    setupTables: {
      title: 'Set up tables for devices',
      titleForConstruct: 'Set up tables for {construct}',
      close: 'Close',
      searchPlaceholder: 'Search in tables',
      currentSettings: 'Current settings',
      availableTables: 'Available tables',
      btnSave: 'Save',
      btnClose: 'Close',
      confirmDialog: {
        header: 'Tables configuration is not saved',
        subHeader: 'Do you really want to leave form without save?',
        btnConfirm: 'Save changes',
        btnSecondaryConfirm: 'Leave',
      },
    },
    pathLookup: {
      title: 'Packet Details',
      headerType: pathLookupHeaderType,
      // SPBM (802.1ah) backbone header — carried as an ethernet header, so it
      // has no distinct `type`; labelled by isPacketHeaderSpbmBackbone instead.
      spbmBackboneHeaderType: 'Backbone',
      trafficScore: {
        title:
          '{dropped, plural, other{#}} of {total, plural, one {# Packet} other {# Packets}} Dropped',
      },
      severityInfo: {
        noIssuesDetected: 'No Issues Detected',
        issuesDetected: 'Issues Detected',
      },
      unknown: 'unknown',
      none: 'None',
      btnNext: 'Next',
      btnPrevious: 'Prev',
    },
    devicePathLookup: {
      decidedByRule: 'Decided by',
      placeholder: {
        tableTitle: 'Decision Table',
      },
      acceptedPacket: 'Accepted Packet',
      droppedPacket: 'Dropped Packet',
      generatedPacket: 'Generated Packet',
      selectPacket: {
        first: {
          step: 'Step 1',
          title: 'Select a Packet at {nodeTitle}',
          incomingTitle: 'Select an Incoming Packet at {nodeTitle}',
          info: 'Select from the list or pick in canvas one of the incoming packets.',
        },
        second: {
          step: 'Step 2',
          title: 'Select a Forwarded or a Terminated Packet ',

          info: 'Select from the list (or pick in canvas) one of the out-going packets that have been forwarded or use the list to pick one of packets that have been terminated (dropped or accepted) by the device.',
        },
        internalPackets: {
          acceptedPackets: 'Accepted packet {index}',
          droppedPackets: 'Dropped packet {index}',
          generatedPackets: 'Generated packet',
        },
        noItemsToShow:
          'No items to show.\nPlease select one of the out-going packets in canvas.',
        sortByPacketsAcceptedAscending: 'Sort By Packets Accepted - Ascending',
        sortByPacketsDroppedAscending: 'Sort By Packets Dropped - Ascending',
        sortByPacketsAcceptedDescending:
          'Sort By Packets Accepted - Descending',
        sortByPacketsDroppedDescending: 'Sort By Packets Dropped - Descending',
        sortByPacketsSeverityAscending: 'Sort By Severity Issues - Ascending',
        sortByPacketsSeverityDescending: 'Sort By Severity Issues - Descending',
        showAcceptedPackets: 'Accepted',
        showDroppedPackets: 'Dropped',
        showForwardedPackets: 'Forwarded',
        sortPackets: 'Sort by Packets Dropped',
      },
      decisionTable: {
        ignoredSecurityCheck: 'security check ignored',
        title: '{nodeTitle} Decision Table',
        showMore: 'Show More',
        showLess: 'Show Less',
        location: 'Location',
        decision: 'Decision',
        filter: 'Filter/Data',
        packetAction: 'Detail / Packet Action',
        phase: {
          in: 'Input Interface',
          core: 'Core',
          out: 'Output Interface',
        },
        pop: 'POP',
        patch: 'PATCH',
        push: 'PUSH',
        insert: 'INSERT',
        remove: 'REMOVE',
        flood: 'FLOODING',
        drop: 'DROP',
        accept: 'ACCEPT',
        interface: 'interface',
        tableEntryMatch: '{table} table match',
        tableEntryNoMatch: '{table} table no match',
        emptyFilterValue: '(empty)',
        events: {
          filter: {
            inIfaceName: 'interface',
            vlanNum: 'vlan',
          },
          severityName: {
            ACL_CONFIG_ERROR: 'ACL configuration error',
            ACL_DENY: 'ACL deny',
            ACL_PARSING_ERROR: 'ACL parsing error',
            ACL_PERMIT: 'ACL permit',
            FLOODING: 'Flooding',
            FORWARDING_ERROR: 'Forwarding error',
            LOOP_DETECTED: 'Traffic loop detected',
            MALFORMED_PACKET: 'Malformed packet',
            NAT44_CONFIG_ERROR: 'NAT (IPv4 to IPv4) configuration error',
            NAT44_MISSING_EVALUATION_RULE:
              'A NAT rule for evaluation could not be found',
            NAT44_PERMIT: 'NAT (IPv4 to IPv4) permit',
            NEXTHOP_NOT_FOUND: 'Nexthop not found',
            NEXTHOP_NULL0_IFACE: 'Nexthop Null0 interface',
            NEXTHOP_SKIP_L3: 'L2 segment resolved (continues at next L3 hop)',
            ROUTING_LOOP_DETECTED: 'Routing loop detected',
            PBR_CONFIG_ERROR: 'PBR configuration error',
            PBR_PERMIT: 'PBR permit',
            ZONEFW_CONFIG_ERROR: 'Zone FW configuration error',
            ZONEFW_DENY: 'Zone FW deny',
            ZONEFW_PARSING_ERROR: 'Zone FW parsing error',
            ZONEFW_PERMIT: 'Zone FW permit',
          },
          topicName: {
            ACL: 'ACL',
            FORWARDING: 'Forwarding',
            NAT44: 'NAT (IPv4 to IPv4)',
            PBR: 'PBR',
            ZONEFW: 'Zone FW',
          },
        },
        tableEntryNotFoundTooltip: 'Table entry not found',
        tableEntryFoundTooltip: 'Table entry found',
        permit: 'permit',
        deny: 'deny',
        notApplied: 'not applied',
      },
    },
  },
};
